import { supabase } from '../supabaseClient'

/**
 * Municipal Civil Registry Service
 * Handles all civil registry-related API calls
 */
export const mcrService = {

  // ==================== CATEGORIES ====================
  
  async getAllCategories() {
    try {
      const { data, error } = await supabase
        .from('mcr_categories')
        .select('*')
        .order('name', { ascending: true })
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching categories:', error)
      return { success: false, error: error.message }
    }
  },

  async addCategory(name) {
    try {
      const { data, error } = await supabase
        .from('mcr_categories')
        .insert([{ name }])
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error adding category:', error)
      return { success: false, error: error.message }
    }
  },

  async updateCategory(id, name) {
    try {
      const { data, error } = await supabase
        .from('mcr_categories')
        .update({ name })
        .eq('id', id)
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating category:', error)
      return { success: false, error: error.message }
    }
  },

  async deleteCategory(id) {
    try {
      const { error } = await supabase
        .from('mcr_categories')
        .delete()
        .eq('id', id)
      
      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error deleting category:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== SUBCATEGORIES ====================
  
  async getAllSubcategories() {
    try {
      const { data, error } = await supabase
        .from('mcr_subcategories')
        .select(`
          *,
          category:mcr_categories(id, name)
        `)
        .order('name', { ascending: true })
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching subcategories:', error)
      return { success: false, error: error.message }
    }
  },

  async getSubcategoriesByCategory(categoryId) {
    try {
      const { data, error } = await supabase
        .from('mcr_subcategories')
        .select('*')
        .eq('category_id', categoryId)
        .order('name', { ascending: true })
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching subcategories:', error)
      return { success: false, error: error.message }
    }
  },

  async addSubcategory(categoryId, name) {
    try {
      const { data, error } = await supabase
        .from('mcr_subcategories')
        .insert([{ category_id: categoryId, name }])
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error adding subcategory:', error)
      return { success: false, error: error.message }
    }
  },

  async updateSubcategory(id, name, categoryId) {
    try {
      const { data, error } = await supabase
        .from('mcr_subcategories')
        .update({ name, category_id: categoryId })
        .eq('id', id)
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating subcategory:', error)
      return { success: false, error: error.message }
    }
  },

  async deleteSubcategory(id) {
    try {
      const { error } = await supabase
        .from('mcr_subcategories')
        .delete()
        .eq('id', id)
      
      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error deleting subcategory:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== COMBINED DATA ====================
  
  async getAllServicesHierarchy() {
    try {
      // Get all categories
      const { data: categories, error: catError } = await supabase
        .from('mcr_categories')
        .select('*')
        .order('name', { ascending: true })
      
      if (catError) throw catError

      // Get all services
      const { data: services, error: srvError } = await supabase
        .from('mcr_services')
        .select('*')
        .order('name', { ascending: true })
      
      if (srvError) throw srvError

      // Build hierarchy (categories with services directly)
      const hierarchy = categories.map(category => ({
        id: category.id,
        name: category.name,
        services: services
          .filter(srv => srv.category_id === category.id)
          .map(srv => ({
            id: srv.id,
            name: srv.name,
            basic_amount: parseFloat(srv.basic_amount || srv.price || 0),
            dst_amount: parseFloat(srv.dst_amount || 0),
            description: srv.description
          }))
      }))

      return { success: true, data: hierarchy }
    } catch (error) {
      console.error('Error fetching services hierarchy:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== SERVICES ====================
  
  async getAllServices() {
    try {
      const { data, error } = await supabase
        .from('mcr_services')
        .select(`
          *,
          subcategory:mcr_subcategories(
            id,
            name,
            category:mcr_categories(id, name)
          )
        `)
        .order('name', { ascending: true })
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching services:', error)
      return { success: false, error: error.message }
    }
  },

  async getServicesBySubcategory(subcategoryId) {
    try {
      const { data, error } = await supabase
        .from('mcr_services')
        .select('*')
        .eq('subcategory_id', subcategoryId)
        .order('name', { ascending: true })
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching services:', error)
      return { success: false, error: error.message }
    }
  },

  async addService(serviceData) {
    try {
      const { data, error } = await supabase
        .from('mcr_services')
        .insert([{
          category_id: serviceData.category_id,
          name: serviceData.name,
          basic_amount: serviceData.basic_amount,
          dst_amount: serviceData.dst_amount || 0,
          description: serviceData.description || null
        }])
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error adding service:', error)
      return { success: false, error: error.message }
    }
  },

  async updateService(id, serviceData) {
    try {
      const { data, error } = await supabase
        .from('mcr_services')
        .update({
          category_id: serviceData.category_id,
          name: serviceData.name,
          basic_amount: serviceData.basic_amount,
          dst_amount: serviceData.dst_amount || 0,
          description: serviceData.description || null
        })
        .eq('id', id)
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating service:', error)
      return { success: false, error: error.message }
    }
  },

  async deleteService(id) {
    try {
      const { error } = await supabase
        .from('mcr_services')
        .delete()
        .eq('id', id)
      
      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error deleting service:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== CLIENTS ====================
  
  async getAllClients() {
    try {
      const { data, error } = await supabase
        .from('mcr_clients')
        .select('*')
        .order('name', { ascending: true })
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching clients:', error)
      return { success: false, error: error.message }
    }
  },

  async searchClients(searchTerm) {
    try {
      const { data, error } = await supabase
        .from('mcr_clients')
        .select('*')
        .ilike('name', `%${searchTerm}%`)
        .order('name', { ascending: true })
        .limit(10)
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error searching clients:', error)
      return { success: false, error: error.message }
    }
  },

  async addClient(clientData) {
    try {
      const { data, error } = await supabase
        .from('mcr_clients')
        .insert([{
          name: clientData.name,
          birthdate: clientData.birthdate,
          age: clientData.age,
          address: clientData.address,
          barangay: clientData.barangay,
          recommended_by: clientData.recommended_by
        }])
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error adding client:', error)
      return { success: false, error: error.message }
    }
  },

  async updateClient(clientId, clientData) {
    try {
      const { data, error } = await supabase
        .from('mcr_clients')
        .update({
          name: clientData.name,
          birthdate: clientData.birthdate,
          age: clientData.age,
          address: clientData.address,
          barangay: clientData.barangay,
          recommended_by: clientData.recommended_by
        })
        .eq('id', clientId)
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating client:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== ORDERS ====================
  
  async getAllOrders() {
    try {
      const { data, error } = await supabase
        .from('mcr_orders')
        .select(`
          *,
          client:mcr_clients(id, name, birthdate, age, address, barangay, recommended_by),
          items:mcr_order_items(*)
        `)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching orders:', error)
      return { success: false, error: error.message }
    }
  },

  async getOrderWithItems(orderId) {
    try {
      const { data: order, error: orderError } = await supabase
        .from('mcr_orders')
        .select(`
          *,
          client:mcr_clients(id, name, birthdate, age, address, barangay, recommended_by)
        `)
        .eq('id', orderId)
        .single()
      
      if (orderError) throw orderError

      const { data: items, error: itemsError } = await supabase
        .from('mcr_order_items')
        .select(`
          *,
          service:mcr_services(
            subcategory:mcr_subcategories(
              name,
              category:mcr_categories(name)
            )
          )
        `)
        .eq('order_id', orderId)
      
      if (itemsError) throw itemsError

      // Enrich items with category/subcategory data if not already present
      const enrichedItems = items.map(item => {
        const category = item.category || item.service?.subcategory?.category?.name || ''
        const subcategory = item.subcategory || item.service?.subcategory?.name || ''
        
        return {
          ...item,
          category,
          subcategory
        }
      })

      return { 
        success: true, 
        data: { 
          ...order, 
          items: enrichedItems || [] 
        } 
      }
    } catch (error) {
      console.error('Error fetching order with items:', error)
      return { success: false, error: error.message }
    }
  },

  async createOrder(orderData) {
    try {
      // Generate order number
      const orderNumber = `MCR-${Date.now()}`

      // Calculate total
      const total = orderData.items.reduce((sum, item) => {
        return sum + (item.unit_price * item.quantity)
      }, 0)

      // Insert order
      const { data: order, error: orderError } = await supabase
        .from('mcr_orders')
        .insert([{
          client_id: orderData.client_id,
          order_number: orderNumber,
          total_amount: total,
          status: 'pending'
        }])
        .select()
        .single()
      
      if (orderError) throw orderError

      // Insert order items
      const items = orderData.items.map(item => ({
        order_id: order.id,
        service_id: item.service_id,
        service_name: item.service_name,
        category: item.category || null,
        subcategory: item.subcategory || null,
        quantity: item.quantity,
        basic_amount: item.basic_amount || 0,
        dst_amount: item.dst_amount || 0,
        unit_price: item.unit_price,
        subtotal: item.unit_price * item.quantity
      }))

      const { error: itemsError } = await supabase
        .from('mcr_order_items')
        .insert(items)
      
      if (itemsError) throw itemsError

      return { success: true, data: order }
    } catch (error) {
      console.error('Error creating order:', error)
      return { success: false, error: error.message }
    }
  },

  async updateOrder(orderId, orderData) {
    try {
      // Calculate total
      const total = orderData.items.reduce((sum, item) => {
        return sum + (item.unit_price * item.quantity)
      }, 0)

      // Update order total
      const { error: orderError } = await supabase
        .from('mcr_orders')
        .update({ total_amount: total })
        .eq('id', orderId)
      
      if (orderError) throw orderError

      // Delete existing order items
      const { error: deleteError } = await supabase
        .from('mcr_order_items')
        .delete()
        .eq('order_id', orderId)
      
      if (deleteError) throw deleteError

      // Insert updated order items
      const items = orderData.items.map(item => ({
        order_id: orderId,
        service_id: item.service_id,
        service_name: item.service_name,
        category: item.category || null,
        subcategory: item.subcategory || null,
        quantity: item.quantity,
        basic_amount: item.basic_amount || 0,
        dst_amount: item.dst_amount || 0,
        unit_price: item.unit_price,
        subtotal: item.unit_price * item.quantity
      }))

      const { error: itemsError } = await supabase
        .from('mcr_order_items')
        .insert(items)
      
      if (itemsError) throw itemsError

      return { success: true, data: { id: orderId } }
    } catch (error) {
      console.error('Error updating order:', error)
      return { success: false, error: error.message }
    }
  },

  async updateOrderStatus(orderId, status, orNumber = null, processedBy = null) {
    try {
      const updateData = { status }
      
      // If status is paid, add OR number, payment timestamp, and processed by
      if (status === 'paid' && orNumber) {
        updateData.or_number = orNumber
        updateData.paid_at = new Date().toISOString()
        if (processedBy) {
          updateData.processed_by = processedBy
        }
      }
      
      const { data, error } = await supabase
        .from('mcr_orders')
        .update(updateData)
        .eq('id', orderId)
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating order status:', error)
      return { success: false, error: error.message }
    }
  },

  async deleteOrder(orderId) {
    try {
      // Delete order items first
      await supabase
        .from('mcr_order_items')
        .delete()
        .eq('order_id', orderId)

      // Delete order
      const { error } = await supabase
        .from('mcr_orders')
        .delete()
        .eq('id', orderId)
      
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting order:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== SEARCH SERVICES ====================
  
  async searchServices(searchTerm) {
    try {
      // Fetch all services with their categories and subcategories
      const { data, error } = await supabase
        .from('mcr_services')
        .select(`
          *,
          subcategory:mcr_subcategories(
            id,
            name,
            category:mcr_categories(id, name)
          )
        `)
        .order('name', { ascending: true })
      
      if (error) throw error
      
      // Filter results to include matches in service name, subcategory name, or category name
      const searchLower = searchTerm.toLowerCase().trim()
      const filteredData = data.filter(service => {
        const serviceName = service.name?.toLowerCase() || ''
        const subcategoryName = service.subcategory?.name?.toLowerCase() || ''
        const categoryName = service.subcategory?.category?.name?.toLowerCase() || ''
        
        return serviceName.includes(searchLower) || 
               subcategoryName.includes(searchLower) || 
               categoryName.includes(searchLower)
      })
      
      // Limit to 20 results
      const limitedData = filteredData.slice(0, 20)
      
      return { success: true, data: limitedData }
    } catch (error) {
      console.error('Error searching services:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== ACCESS CODES ====================
  
  async verifyAccessCode(code) {
    try {
      const { data, error } = await supabase
        .from('mcr_access_codes')
        .select('*')
        .eq('code', code.trim())
        .eq('is_active', true)
        .single()
      
      if (error) {
        if (error.code === 'PGRST116') {
          return { success: false, error: 'Invalid access code' }
        }
        throw error
      }
      
      return { success: true, data }
    } catch (error) {
      console.error('Error verifying access code:', error)
      return { success: false, error: error.message }
    }
  },

  async updateAccessCode(currentCode, newCode) {
    try {
      // First verify current code
      const verifyResult = await this.verifyAccessCode(currentCode)
      if (!verifyResult.success) {
        return { success: false, error: 'Current access code is incorrect' }
      }

      // Update the code
      const { data, error } = await supabase
        .from('mcr_access_codes')
        .update({ 
          code: newCode.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('code', currentCode.trim())
        .eq('is_active', true)
        .select()
      
      if (error) throw error
      
      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating access code:', error)
      return { success: false, error: error.message }
    }
  }
}
